import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { Package, ArrowSquareOut } from "@phosphor-icons/react";
import { inr } from "./CostSummary";

const STATUS_COLORS = { draft: "var(--c-muted)", inquiry_created: "var(--c-accent)", quoted: "var(--c-green)", ordered: "var(--c-amber)", received: "var(--c-green)", closed: "var(--c-green)", cancelled: "var(--c-red)" };
const SIGNOFF = ["scm_manager", "lead", "project_manager", "admin"];

export default function BomPrs({ projectId, me }) {
  const [prs, setPrs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState("");
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get(`/bom/${projectId}/prs`);
      setPrs(data.prs);
      setSummary(data.summary);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, [projectId]);

  const createInquiry = async (pr) => {
    setBusy(pr.pr_id);
    try {
      const { data } = await api.post(`/prs/${pr.pr_id}/create-inquiry`);
      toast.success("SCM inquiry created");
      await load();
      nav(`/scm/inquiries/${data.inquiry_id}`);
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(""); }
  };

  const setStatus = async (pr, status) => {
    try {
      await api.patch(`/prs/${pr.pr_id}`, { status });
      toast.success(`PR → ${status}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const filtered = filter ? prs.filter((p) => p.status === filter) : prs;
  const canInquire = me && SIGNOFF.includes(me.role);

  return (
    <div className="space-y-3" data-testid="pr-panel">
      {summary && (
        <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-3 flex flex-wrap gap-4 items-center" data-testid="pr-summary">
          <span className="text-[11px] text-[var(--c-text)]">Purchase Requisitions: <b>{summary.total}</b></span>
          {[["draft", "Draft"], ["inquiry_created", "Inquiry Created"], ["quoted", "Quoted"], ["ordered", "Ordered"], ["received", "Received"]].map(([k, l]) => (
            <span key={k} className="text-[10px] font-mono" style={{ color: STATUS_COLORS[k] }}>{l}: {summary[k] || 0}</span>
          ))}
          <select data-testid="pr-status-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
            className="ml-auto h-7 bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] text-[11px] rounded-sm px-2">
            <option value="">All statuses</option>
            {Object.keys(STATUS_COLORS).map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        </div>
      )}
      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[var(--c-border)] text-left font-mono text-[9px] uppercase tracking-wider text-[var(--c-muted)]">
              <th className="py-2 px-2">PR ID</th><th className="py-2 px-2">Item</th><th className="py-2 px-2">Spec</th>
              <th className="py-2 px-2 text-right">Qty</th><th className="py-2 px-2 text-right">Est Cost</th>
              <th className="py-2 px-2">Vendor</th><th className="py-2 px-2">Required By</th><th className="py-2 px-2">SCM Inquiry</th>
              <th className="py-2 px-2">Status</th><th className="py-2 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((pr) => (
              <tr key={pr.pr_id} className="border-b border-[var(--c-border)]/40 hover:bg-[var(--c-border)]/25" data-testid={`pr-row-${pr.pr_id}`}>
                <td className="py-1.5 px-2 font-mono text-[10px] text-[var(--c-accent)]">{pr.pr_id}</td>
                <td className="py-1.5 px-2 text-[11px] text-[var(--c-text)]">{pr.description}
                  <div className="text-[9px] text-[var(--c-muted)] font-mono">BOM {pr.bom_item_number}</div></td>
                <td className="py-1.5 px-2 text-[10px] text-[var(--c-muted)] max-w-[200px] truncate">{pr.material_spec}</td>
                <td className="py-1.5 px-2 text-right font-mono text-[11px]">{pr.quantity} {pr.unit}</td>
                <td className="py-1.5 px-2 text-right font-mono text-[11px]">{inr(pr.estimated_cost)}</td>
                <td className="py-1.5 px-2 text-[10px] text-[var(--c-muted)]">{pr.suggested_vendor || "—"}</td>
                <td className="py-1.5 px-2 text-[10px] font-mono text-[var(--c-muted)]">{pr.required_by_date}</td>
                <td className="py-1.5 px-2">
                  {pr.linked_scm_inquiry_id ? (
                    <button className="text-[10px] text-[var(--c-accent)] hover:underline flex items-center gap-1"
                      onClick={() => nav(`/scm/inquiries/${pr.linked_scm_inquiry_id}`)}
                      data-testid={`pr-inquiry-${pr.pr_id}`}>
                      <ArrowSquareOut size={10} /> Linked
                    </button>
                  ) : <span className="text-[10px] text-[var(--c-muted)]">—</span>}
                </td>
                <td className="py-1.5 px-2">
                  <select data-testid={`pr-status-${pr.pr_id}`} value={pr.status}
                    onChange={(e) => setStatus(pr, e.target.value)}
                    className="h-6 bg-[var(--c-bg)] border border-[var(--c-border)] text-[10px] rounded-sm px-1"
                    style={{ color: STATUS_COLORS[pr.status] }}>
                    {Object.keys(STATUS_COLORS).map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                </td>
                <td className="py-1.5 px-2">
                  {!pr.linked_scm_inquiry_id && canInquire && !pr.standard_stock && (
                    <button data-testid={`pr-create-inquiry-${pr.pr_id}`} disabled={busy === pr.pr_id}
                      onClick={() => createInquiry(pr)}
                      className="h-6 px-2 rounded-sm bg-[var(--c-accent)]/15 text-[var(--c-accent)] border border-[var(--c-accent)]/40 text-[10px] flex items-center gap-1 disabled:opacity-40">
                      <Package size={10} /> {busy === pr.pr_id ? "Creating…" : "Create Inquiry"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr><td colSpan={10} className="py-8 text-center text-[11px] text-[var(--c-muted)]">No purchase requisitions. Generate the BOM first.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
